// CustomerNameAutocomplete — the customer NAME input doubles as the
// autocomplete search trigger.
//
// Mirrors the legacy `setupCustomerAutocomplete()` in
// `../tricivo_system/operations/js/autocomplete.js`: typing in the name
// field debounces a 300ms request to GET /customers?search=, and the
// results render in a dropdown directly below the input. Selecting a
// result fires `onSelectCustomer` with the full CustomerSummary so the
// parent form can populate all the other fields. Typing without picking
// from the dropdown emits `onChange` so the literal name becomes the
// new-customer name and `customer_id` stays null on submit.
//
// The linked indicator + Change button is rendered when `linkedCustomer`
// is provided so the user can detach the chosen customer to start over.
//
// `customerLinkedRef` ensures the dropdown does NOT reopen automatically
// when the parent form sets the name from a linked customer.

import { useEffect, useRef, useState } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '../../../components/form/Input';
import { useDebounce } from '../../../hooks/useDebounce';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type {
  CustomerListResponse,
  CustomerSummary,
} from '../../../types/api';
import styles from './CustomerNameAutocomplete.module.css';

const MIN_QUERY = 2;
const PAGE_SIZE = 10;

export interface CustomerNameAutocompleteProps {
  /** Current value of the name field. */
  value: string;
  /** Fires when the user types — emits the new typed value. */
  onChange: (value: string) => void;
  /** Fires when the user clicks a result. */
  onSelectCustomer: (customer: CustomerSummary) => void;
  /** When set, render a "linked" affordance + Change button. */
  linkedCustomer?: CustomerSummary | null;
  /** Fired when the user clicks Change. The parent should clear its
   *  linked customer so the user can search/type again. */
  onClearLinked?: () => void;
  /** Optional input id for label association — defaults to "customer-name". */
  inputId?: string;
  /** Required label — usually "Customer name" / "Name". */
  label?: string;
  /** Mark required for screen readers. */
  required?: boolean;
  /** Disable the input (e.g. when read-only). */
  disabled?: boolean;
}

async function fetchCustomers(search: string): Promise<CustomerSummary[]> {
  const res = await apiClient.get<CustomerListResponse>(
    endpoints.customers.list,
    { params: { search, limit: PAGE_SIZE } },
  );
  return res.data?.customers ?? [];
}

export function CustomerNameAutocomplete({
  value,
  onChange,
  onSelectCustomer,
  linkedCustomer = null,
  onClearLinked,
  inputId = 'customer-name',
  label = 'Customer name',
  required,
  disabled,
}: CustomerNameAutocompleteProps): JSX.Element {
  const [open, setOpen] = useState(false);
  // Track whether the latest value change came from a programmatic
  // selection so we don't re-open the dropdown after a pick.
  const justSelectedRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const debounced = useDebounce(value, 300);

  const enabled =
    linkedCustomer === null &&
    !justSelectedRef.current &&
    open &&
    debounced.trim().length >= MIN_QUERY;

  const query = useQuery({
    queryKey: queryKeys.customers.list({ search: debounced }),
    queryFn: () => fetchCustomers(debounced.trim()),
    enabled,
    staleTime: 30_000,
  });

  // Outside-click closes the dropdown.
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleInputChange(next: string) {
    justSelectedRef.current = false;
    onChange(next);
    if (next.trim().length >= MIN_QUERY && !linkedCustomer) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }

  function handlePick(c: CustomerSummary) {
    justSelectedRef.current = true;
    setOpen(false);
    onSelectCustomer(c);
  }

  const results = query.data ?? [];
  const showResults =
    open && enabled && (query.isLoading || results.length > 0 || query.isError);

  return (
    <div className={styles.wrap} ref={wrapperRef}>
      <div className={styles.inputWrap}>
        <Input
          id={inputId}
          label={label}
          required={required}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => {
            if (!linkedCustomer && value.trim().length >= MIN_QUERY) {
              setOpen(true);
            }
          }}
          autoComplete="off"
          aria-label={label}
          disabled={disabled}
        />
        {linkedCustomer && onClearLinked ? (
          <button
            type="button"
            className={styles.clearLinked}
            onClick={() => {
              onClearLinked();
              setOpen(false);
            }}
            aria-label="Clear linked customer"
          >
            <X size={14} aria-hidden /> Change
          </button>
        ) : null}
      </div>

      {showResults ? (
        <div role="listbox" className={styles.results}>
          {query.isLoading ? (
            <div className={styles.empty}>Searching…</div>
          ) : query.isError ? (
            <div className={styles.empty}>Could not search customers.</div>
          ) : results.length === 0 ? (
            <div className={styles.empty}>
              No matching customers — typing a new name creates one.
            </div>
          ) : (
            <>
              <div className={styles.resultsHeader}>
                <SearchIcon size={12} aria-hidden /> {results.length} existing
                customer{results.length === 1 ? '' : 's'}
              </div>
              {results.map((c) => (
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  key={c.uuid}
                  className={styles.result}
                  onClick={() => handlePick(c)}
                >
                  <span className={styles.resultName}>{c.full_name}</span>
                  <span className={styles.resultMeta}>
                    {c.phone}
                    {c.email ? ` · ${c.email}` : ''}
                  </span>
                  {c.total_jobs > 0 ? (
                    <span className={styles.resultBadge}>
                      {c.total_jobs} previous booking
                      {c.total_jobs === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </button>
              ))}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
